import React from "react";
import { Image, View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { MessageSquare, Plus } from "lucide-react-native";
import {
  CardTitleText,
  DocumentRow,
  DateField,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import type { PapSmearProcedureDetail } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { PAP_SMEAR_PROCEDURE_OPTIONS } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

const testResultIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;

export function PapSmearProcedureDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  const getProcedureLabel = (procedure: string): string => {
    const option = PAP_SMEAR_PROCEDURE_OPTIONS.find((o) => o.id === procedure);
    return option ? t(option.labelKey) : procedure;
  };

  const updateProcedure = (
    index: number,
    updates: Partial<PapSmearProcedureDetail>,
  ) => {
    const updated = state.papSmearProcedures.map((p, i) =>
      i === index ? { ...p, ...updates } : p,
    );
    updateState({ papSmearProcedures: updated });
  };

  const addProcedure = (procedure: PapSmearProcedureDetail["procedure"]) => {
    updateState({
      papSmearProcedures: [...state.papSmearProcedures, { procedure }],
    });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("PapSmearProcedureDetails");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  // Group procedures by type for the repeater
  const procedureTypes = [
    ...new Set(state.papSmearProcedures.map((p) => p.procedure)),
  ];

  return (
    <QuestionLayout
      title={t(
        "med_history.gynae.sexual_health.pap_smear_procedure_details_title",
      )}
      subtitle={t(
        "med_history.gynae.sexual_health.pap_smear_procedure_details_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        {procedureTypes.map((procedureType) => {
          const proceduresOfType = state.papSmearProcedures
            .map((p, i) => ({ ...p, _index: i }))
            .filter((p) => p.procedure === procedureType);

          return (
            <View key={procedureType} style={styles.procedureGroup}>
              {proceduresOfType.map((proc, groupIdx) => (
                <View
                  key={proc._index}
                  style={[cardSurface(), styles.procedureCard]}
                >
                  <CardTitleText>
                    {`${getProcedureLabel(procedureType)}${proceduresOfType.length > 1 ? ` ${groupIdx + 1}` : ""}`}
                  </CardTitleText>
                  <DateField
                    label={t(
                      "med_history.gynae.sexual_health.procedure_date_label",
                    )}
                    value={proc.procedureDate ?? ""}
                    onChange={(iso) =>
                      updateProcedure(proc._index, {
                        procedureDate: iso || null,
                      })
                    }
                  />
                  <MedicalHistoryTextField
                    label={t("med_history.gynae.sexual_health.notes_label")}
                    labelIcon={MessageSquare}
                    placeholder={t(
                      "med_history.gynae.sexual_health.notes_placeholder",
                    )}
                    value={proc.notes ?? ""}
                    onChangeText={(text) =>
                      updateProcedure(proc._index, {
                        notes: text || null,
                      })
                    }
                    multiline
                    numberOfLines={2}
                    style={{ width: "100%" }}
                  />
                </View>
              ))}

              <DocumentRow
                onPress={() => addProcedure(procedureType)}
                accessibilityLabel={t(
                  "med_history.gynae.sexual_health.add_another",
                  {
                    params: {
                      name: getProcedureLabel(procedureType).toLowerCase(),
                    },
                  },
                )}
                leading={
                  <Image
                    source={testResultIllustration}
                    resizeMode="contain"
                    style={styles.addIllustration}
                  />
                }
                title={t("med_history.gynae.sexual_health.add_another", {
                  params: {
                    name: getProcedureLabel(procedureType).toLowerCase(),
                  },
                })}
                trailing={
                  <Plus
                    size={18}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.5}
                  />
                }
                showChevron={false}
                style={styles.addRow}
              />
            </View>
          );
        })}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 24,
  },
  procedureGroup: {
    gap: 12,
  },
  procedureCard: {
    gap: 16,
    padding: 16,
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});
